import {
  Alien,
  ArrowsDownUp,
  Calculator,
  CubeFocus,
  EnvelopeSimple,
  GearSix,
  Globe,
  Intersect,
  MapPin,
  Package,
  PencilCircle,
  PersonArmsSpread,
  PuzzlePiece,
  Users,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
// Deep import: the `companySettings/routes` barrel also pulls the route table, and with it every
// page and component of this module — the menu only needs the path builders.
import {
  companyImportExportApprovedVendorsPath,
  companyImportExportExtractorVendorMappingPath,
  companyImportExportUomConversionAuditPath,
  oldAdminEmailTemplatesPath,
  oldAdminExternalPartsPath,
  oldAdminFormattingColorsPath,
  oldAdminFormattingFontsPath,
  oldAdminFormattingLogosPath,
  oldAdminGeneralSettingsPath,
  oldAdminIntegrationsPath,
  oldAdminInventoryFieldMappingPath,
  oldAdminInventoryLogPath,
  oldAdminJobExportFieldMappingPath,
  oldAdminLaborRatesPath,
  oldAdminLocationsPath,
  oldAdminManufacturerAliasesPath,
  oldAdminPartsPath,
  oldAdminQuoteExportMappingPath,
  oldAdminRolesPath,
  oldAdminSetUpPath,
  oldAdminSupportRequestsPath,
  oldAdminUsersPath,
  partFinderAttributeListPath,
  partFinderFamilyListPath,
  partFinderProductTypeListPath,
} from 'companySettings/routes/routePaths';
import { canAccessExtractorVendorMapping, canAccessPartFinderSetup } from 'companySettings/utils';
import { MenuItem, MenuList } from 'core/components';

export const LeftMenu = () => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  const menuItems: MenuItem[] = [
    {
      label: t('labelGeneralSettings'),
      icon: <Globe />,
      href: oldAdminGeneralSettingsPath(),
    },
    {
      label: t('labelSetUp'),
      icon: <GearSix />,
      href: oldAdminSetUpPath(),
      hasCaret: true,
    },
    {
      label: t('labelFormatting'),
      icon: <PencilCircle />,
      hasCaret: true,
      items: [
        {
          label: t('labelColors'),
          href: oldAdminFormattingColorsPath(),
        },
        {
          label: t('labelFonts'),
          href: oldAdminFormattingFontsPath(),
        },
        {
          label: t('labelLogos'),
          href: oldAdminFormattingLogosPath(),
        },
      ],
    },
    {
      label: t('labelUsers'),
      icon: <Users />,
      href: oldAdminUsersPath(),
    },
    {
      label: t('labelLocations'),
      icon: <MapPin />,
      href: oldAdminLocationsPath(),
    },
    {
      label: t('labelRoles'),
      icon: <Alien />,
      href: oldAdminRolesPath(),
      hasCaret: true,
    },
    {
      label: t('labelLaborRates'),
      icon: <Calculator />,
      href: oldAdminLaborRatesPath(),
    },
    {
      label: t('labelSupportRequests'),
      icon: <PersonArmsSpread />,
      to: oldAdminSupportRequestsPath(),
      hasCaret: true,
    },
    {
      label: t('labelParts'),
      icon: <CubeFocus />,
      href: oldAdminPartsPath(),
    },
    {
      label: t('labelExternalParts'),
      icon: <Package />,
      href: oldAdminExternalPartsPath(),
    },
    {
      label: t('labelImportExport'),
      icon: <ArrowsDownUp />,
      hasCaret: true,
      items: [
        {
          label: t('labelInventoryLog'),
          href: oldAdminInventoryLogPath(),
        },
        {
          label: t('labelInventoryFieldMapping'),
          href: oldAdminInventoryFieldMappingPath(),
        },
        {
          label: t('labelManufacturerAliases'),
          href: oldAdminManufacturerAliasesPath(),
        },
        {
          label: t('labelQuoteExportMapping'),
          href: oldAdminQuoteExportMappingPath(),
        },
        {
          label: t('labelJobExportFieldMapping'),
          href: oldAdminJobExportFieldMappingPath(),
        },
        {
          label: t('labelApprovedVendors'),
          to: companyImportExportApprovedVendorsPath(),
        },
        {
          label: t('titleExtractorVendorMapping'),
          to: companyImportExportExtractorVendorMappingPath(),
          hide: !canAccessExtractorVendorMapping(user),
        },
        {
          label: t('labelUomConversionAudit'),
          to: companyImportExportUomConversionAuditPath(),
        },
      ],
    },
    {
      label: t('labelIntegrations'),
      icon: <Intersect />,
      href: oldAdminIntegrationsPath(),
      hasCaret: true,
    },
    {
      label: t('labelEmailTemplates'),
      icon: <EnvelopeSimple />,
      href: oldAdminEmailTemplatesPath(),
      hasCaret: true,
    },
    {
      label: t('labelPartFinder'),
      icon: <PuzzlePiece />,
      hasCaret: true,
      hide: !canAccessPartFinderSetup(user),
      items: [
        {
          label: t('labelPartFinderAttributes'),
          to: partFinderAttributeListPath(),
        },
        {
          label: t('labelPartFinderProductTypes'),
          to: partFinderProductTypeListPath(),
        },
        {
          label: t('labelPartFinderFamilies'),
          to: partFinderFamilyListPath(),
        },
      ],
    },
  ];

  return <MenuList items={menuItems} />;
};
